<script lang="ts" setup>
import type SwiperCore from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Keyboard, Pagination } from 'swiper/modules'
import { useGalleryState } from '@/composables/useGalleryState'
import ImageSwiperItem from '@/components/ImageSwiperItem.vue'
import { ref, onMounted } from 'vue'
import 'swiper/scss'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import { userInteractionEvent } from '@/composables/userInteractionEvent'
const { INTERACTION_KEYPRESS } = userInteractionEvent()

const emit = defineEmits<{
  (e: 'slideChange', data: SwiperCore): void
  (e: 'onInteraction', interactionSource: string): void
}>()

const modules = [Navigation, Keyboard, Pagination]
const keyboardConfig = {
  enabled: true,
}

const { activeSlide, gallery, isNavigationReloadForced } = useGalleryState()

const swiperInstance = ref<SwiperCore | null>(null)

const onSwiper = (instance: SwiperCore) => {
  swiperInstance.value = instance
}
const onSlideChange = (swiper: SwiperCore) => {
  activeSlide.value = swiper.realIndex
  emit('slideChange', swiper)
}

const onNavigationClick = (swiper: SwiperCore) => {
  isNavigationReloadForced(swiper)
}

const onKeyPress = (swiper: SwiperCore) => {
  if (isNavigationReloadForced(swiper)) return
  emit('onInteraction', INTERACTION_KEYPRESS)
}

onMounted(() => {
  // fix for incorrect width on load
  for (const image of Array.from(document.querySelectorAll('.swiper-container img'))) {
    image.addEventListener('load', () => {
      if (swiperInstance.value) swiperInstance.value.update()
    })
  }
})

const slidePrev = () => {
  if (swiperInstance.value) swiperInstance.value.slidePrev()
}
const slideNext = () => {
  if (swiperInstance.value) swiperInstance.value.slideNext()
}
const slideTo = (slide: number) => {
  if (swiperInstance.value) {
    swiperInstance.value.slideTo(slide + 1, 0) // swiper js is not using zero based index but slide number
  }
}
defineExpose({
  slidePrev,
  slideNext,
  slideTo,
})
</script>

<template>
  <div>
    <swiper
      :initialSlide="activeSlide"
      :keyboard="keyboardConfig"
      :slides-per-view="1"
      :space-between="10"
      :navigation="true"
      :pagination="{ type: 'progressbar' }"
      :lazy="true"
      @swiper="onSwiper"
      @slideChange="onSlideChange"
      @keyPress="onKeyPress"
      @navigationNext="onNavigationClick"
      @navigationPrev="onNavigationClick"
      auto-height
      loop
      watch-overflow
      class="swiper-images"
      :modules="modules"
      :style="{
        '--swiper-navigation-color': '#fff',
        '--swiper-pagination-color': '#cf2528',
        '--swiper-theme-color': '#cf2528',
      }"
    >
      <swiper-slide v-for="(image, index) in gallery.images" :key="index">
        <image-swiper-item :key="index" :image="image"></image-swiper-item>
      </swiper-slide>
    </swiper>
  </div>
</template>
